import { useCallback, useMemo, useState } from "react";
import { Flex, ScrollArea, Skeleton } from "@radix-ui/themes";
import { EyeClosedIcon } from "@radix-ui/react-icons";
import { buildColorMap } from "@/components/radixColors";
import {
  Collaborator,
  ExploreInteractionRow,
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import { QaScore, QuestionType } from "@/v2/entities/qa-score/QaScore.model";
import { InteractionViewTabContentEmptyState } from "../parts/InteractionViewTabContentEmptyStateProps";
import { EvaluationTabHeader } from "./EvaluationTabHeader";
import { EvaluationTabItem, EvaluationTabs } from "./EvaluationTabs";
import { TicketScore } from "../../qa-score/TicketScore";
import { ConversationScore } from "../../qa-score/ConversationScore";
import { EvaluationScorecardTabs } from "./EvaluationScorecardTabs";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";

interface EvaluationProps {
  scores: QaScore[];
  collaborators: Collaborator[];
  source: ExploreInteractionType;
  interaction: ExploreInteractionRow;
  isLoading?: boolean;
  onUpdateInteractionScore: () => Promise<unknown>;
}

interface ScorecardSection {
  id: number;
  name: string;
  type: QuestionType;
  scores: QaScore[];
  collaborators: Collaborator[];
  averageScore: number;
}

const calcAvgQaScore = (scores: QaScore[], formClientType: QuestionType) => {
  const scoresToCalc = scores.filter((score) => score.adjustedScore >= 0);

  const totalQaScore = scoresToCalc.reduce((sum, score) => {
    if (score.type === "binary") {
      // Convert 0-1 rating to 1-5 scale
      return sum + (score.adjustedScore * 4 + 1 || 1);
    }

    // Rating scores stay as-is
    return sum + (score.adjustedScore === 0 ? 0 : score.adjustedScore || 1);
  }, 0);

  const avgQaScore = scoresToCalc.length
    ? totalQaScore / scoresToCalc.length
    : 0;

  if (formClientType === "binary") {
    // Convert 1-5 scale to 0-1 scale
    return (avgQaScore - 1) / 4;
  }

  return avgQaScore;
};

export function Evaluation({
  source,
  interaction,
  scores,
  collaborators,
  isLoading,
  onUpdateInteractionScore,
}: EvaluationProps) {
  // Group scores by sampleForm.id
  const scoresBySampleForm = useMemo(() => {
    const grouped = new Map<number, QaScore[]>();

    scores.forEach((score) => {
      if (score.sampleForm) {
        const formId = score.sampleForm.id;
        if (!grouped.has(formId)) {
          grouped.set(formId, []);
        }
        grouped.get(formId)!.push(score);
      }
    });

    return grouped;
  }, [scores]);

  // Get unique sample forms with their data
  const sampleFormSections = useMemo<ScorecardSection[]>(() => {
    const forms = new Map<
      number,
      { id: number; name: string; type: QuestionType }
    >();

    scores.forEach((score) => {
      if (score.sampleForm) {
        forms.set(score.sampleForm.id, {
          id: score.sampleForm.id,
          name: score.sampleForm.name,
          type: score.sampleForm.clientType,
        });
      }
    });

    return Array.from(forms.values()).map((form) => {
      const formScores = scoresBySampleForm.get(form.id) || [];
      const averageScore = calcAvgQaScore(formScores, form.type);

      // Filter collaborators that have scores in this sample form
      const formCollaborators = collaborators.filter((collaborator) =>
        formScores.some((score) => score.agentId === collaborator.agentId)
      );

      return {
        id: form.id,
        name: form.name,
        type: form.type,
        scores: formScores,
        collaborators: formCollaborators,
        averageScore,
      };
    });
  }, [scores, scoresBySampleForm, collaborators]);

  if (isLoading) {
    return (
      <Flex height={"100%"} pt={"4"} direction={"column"} overflow={"hidden"}>
        <Flex px={"2"}>
          <Skeleton className="w-full h-[55px] rounded-lg" />
        </Flex>

        <ScrollArea scrollbars={"vertical"}>
          <Flex py={"4"} px={"6"} direction={"column"} gap="4">
            <Flex direction={"column"}>
              <Skeleton className="w-[100px] h-[18px] rounded-md m-1" />
              <Skeleton className="w-[120px] h-[12px] rounded-md m-1" />
            </Flex>

            <Flex direction={"column"}>
              <Skeleton className="w-[160px] h-[12px] rounded-md m-1" />
              <Skeleton className="w-[400px] h-[12px] rounded-md m-1" />
            </Flex>
          </Flex>
        </ScrollArea>
      </Flex>
    );
  }

  if (!scores.length || !sampleFormSections.length) {
    return (
      <InteractionViewTabContentEmptyState
        icon={<EyeClosedIcon />}
        title={"Evaluation not yet available"}
        description={`An evaluation will be generated once the ${exploreInteractionTypeToLabel[
          source
        ].singular.toLowerCase()} reaches its final resolution. Check back later to review insights and results.`}
      />
    );
  }

  return (
    <Flex height={"100%"} direction={"column"} overflow={"hidden"}>
      <ScorecardView
        sampleFormSections={sampleFormSections}
        source={source}
        interaction={interaction}
        onUpdateInteractionScore={onUpdateInteractionScore}
      />
    </Flex>
  );
}

interface ScorecardViewProps {
  sampleFormSections: ScorecardSection[];
  source: ExploreInteractionType;
  interaction: ExploreInteractionRow;
  onUpdateInteractionScore: () => Promise<unknown>;
}

function ScorecardView({
  sampleFormSections,
  source,
  interaction,
  onUpdateInteractionScore,
}: ScorecardViewProps) {
  const compareFn = useCallback((a: QaScore, b: QaScore) => {
    return a.id - b.id;
  }, []);
  const [selectedScorecardTab, setSelectedScorecardTab] = useState<string>(
    sampleFormSections.sort((a, b) => b.name.localeCompare(a.name))[0]?.id?.toString() || ""
  );

  const scorecardTabs = useMemo<EvaluationTabItem[]>(() => {
    const colorMap = buildColorMap(
      sampleFormSections.map((section) => section.name)
    );

    return sampleFormSections.map<EvaluationTabItem>((section) => ({
      value: section.id.toString(),
      title: section.name,
      color: colorMap[section.name] || "gray",
      publicCommentsCount: undefined,
      avgQaScore: section.averageScore,
      totalAdjusted: section.scores.filter(
        (score) => score.autoScore !== score.adjustedScore
      ).length,
      scores: section.scores,
    }));
  }, [sampleFormSections]);

  const selectedScorecard = useMemo(() => {
    return sampleFormSections.find(
      (section) => section.id.toString() === selectedScorecardTab
    );
  }, [sampleFormSections, selectedScorecardTab]);

  if (!selectedScorecard) {
    return null;
  }

  return (
    <Flex direction="column" gap="4" height={"100%"}>
      <Flex px={"2"}>
        {/* Scorecard Tabs */}
        <EvaluationScorecardTabs
          tabs={scorecardTabs}
          value={selectedScorecardTab}
          onChange={setSelectedScorecardTab}
        />
      </Flex>

      {/* Scorecard Content */}
      <Flex overflow={"hidden"}>
        <ScorecardContent
          sampleForm={selectedScorecard}
          source={source}
          interaction={interaction}
          onUpdateInteractionScore={onUpdateInteractionScore}
          compareFn={compareFn}
          normalizeScoreToOne={false}
        />
      </Flex>
    </Flex>
  );
}

interface ScorecardContentProps {
  sampleForm: ScorecardSection;
  source: ExploreInteractionType;
  interaction: ExploreInteractionRow;
  onUpdateInteractionScore: () => Promise<unknown>;
  compareFn: (a: QaScore, b: QaScore) => number;
  normalizeScoreToOne?: boolean;
}

function ScorecardContent({
  sampleForm,
  source,
  interaction,
  onUpdateInteractionScore,
  compareFn,
  normalizeScoreToOne,
}: ScorecardContentProps) {
  const [selectedTabValue, setSelectedTabValue] = useState<string>(source);

  const displayDuplicateQaQuestions = useFlag("displayDuplicateQaQuestions", false);

  const tabs = useMemo<EvaluationTabItem[]>(() => {
    let unknownAgentIndex = 1;
    const collaboratorsWithName = sampleForm.collaborators.map(
      (collaborator) => {
        return {
          ...collaborator,
          agentName:
            collaborator.agentName != null &&
            collaborator.agentName.trim() !== ""
              ? collaborator.agentName
              : `Unknown Agent #${unknownAgentIndex++}`,
        };
      }
    );

    const colorMap = buildColorMap(
      collaboratorsWithName.map((collaborator) => collaborator.agentName)
    );

    const interactionScoreEntity =
      source === "tickets" ? "ticket" : "conversation";

    const interactionScores = sampleForm.scores.filter(
      ({ scoreEntity }) => scoreEntity === interactionScoreEntity
    );

    // Calculate average specifically for interaction scores
    const interactionAverageScore = calcAvgQaScore(
      interactionScores,
      sampleForm.type
    );

    const interactionTab: EvaluationTabItem = {
      value: source,
      title: exploreInteractionTypeToLabel[source].singular,
      color: "gray",
      publicCommentsCount: interaction.countPublicMessages ?? undefined,
      avgQaScore: interactionAverageScore,
      scores: interactionScores,
    };

    const agentTabs = collaboratorsWithName.map<EvaluationTabItem>(
      (collaborator) => {
        const agentScores = sampleForm.scores.filter(
          ({ scoreEntity, agentId }) =>
            scoreEntity === "agent" && agentId === collaborator.agentId
        );

        const avgQaScore = calcAvgQaScore(agentScores, sampleForm.type);

        const totalAdjusted = sampleForm.scores.filter(
          (score) =>
            score.agentId === collaborator.agentId &&
            score.autoScore !== score.adjustedScore
        ).length;

        return {
          value: `agent-${collaborator.agentId}`,
          title: collaborator.agentName,
          color: colorMap[collaborator.agentName],
          publicCommentsCount: collaborator.publicCommentsCount,
          avgQaScore,
          totalAdjusted,
          scores: agentScores,
        };
      }
    );

    return [
      interactionTab,
      ...agentTabs.sort((a, b) => (b.avgQaScore ?? 0) - (a.avgQaScore ?? 0)),
    ];
  }, [sampleForm, source, interaction]);

  const selectedTab = useMemo(() => {
    return tabs.find((tab) => tab.value === selectedTabValue);
  }, [tabs, selectedTabValue]);

  return (
    <Flex direction="column" gap="4" height={"100%"} overflow={"hidden"}>
      {/* Agent Tabs */}
      <Flex px={"2"}>
        <EvaluationTabs
          tabs={tabs}
          value={selectedTabValue}
          onChange={setSelectedTabValue}
        />
      </Flex>

      {/* Content */}
      <ScrollArea scrollbars={"vertical"}>
        <Flex direction="column" gap="4" py={"4"} px={"6"}>
          {selectedTab && <EvaluationTabHeader {...selectedTab} />}

          {selectedTab?.scores
            .sort(compareFn)
            // Deduplicate by question title and content to avoid showing the same question multiple times
            .filter((score, index, array) => {
              if (displayDuplicateQaQuestions) {
                return true;
              }

              const isDuplicate = array
                .slice(0, index)
                .some(
                  (prevScore) =>
                    prevScore.title === score.title &&
                    prevScore.content === score.content
                );
              return !isDuplicate;
            })
            .map((score) => (
              <Flex key={score.id}>
                {source === "tickets" && (
                  <TicketScore
                    {...score}
                    onUpdateTicketScore={onUpdateInteractionScore}
                  />
                )}

                {source === "conversations" && (
                  <ConversationScore
                    {...score}
                    onUpdateConversationScore={onUpdateInteractionScore}
                  />
                )}
              </Flex>
            ))}
        </Flex>
      </ScrollArea>
    </Flex>
  );
}
